/* Strategy Library — 35 option strategies with full metadata.

   Each entry defines everything the Library UI needs:
     id           stable key used for URL hash / planner pre-fill
     name         display title
     category     1..8 grouping (BASIC / VERTICAL / NEUTRAL_VOL / CONDOR /
                  BUTTERFLY / LADDER / RATIO / SPECIALIST)
     sentiment    "bullish" | "bearish" | "neutral" | "vol"
     sentimentIcon emoji from user spec
     difficulty   "beginner" | "intermediate" | "advanced"
     risk         "defined" | "undefined"
     plannerKind  if set, planner dropdown value that pre-fills this strat;
                  when null → "Planner support coming soon" label
     net          "debit" | "credit" | "mixed"
     short        one-line blurb for the card
     whatIs       plain-English "what it is" paragraph
     whenToUse    market conditions
     legs         array of {side, kind, strike?} describing each contract
     maxProfit    string formula (e.g. "Unlimited")
     maxLoss      string formula
     breakeven    string formula (comma-separated if 2 BEs)
     pros         short bullet array
     cons         short bullet array
     example      {stock, entry, legs, outcome} — filled from live price

   `example` takes the live current price and fills in realistic strikes
   around it. `payoff(S, strikes, premium)` returns dollar P/L at expiry
   for that strategy — used to draw the SVG payoff diagram. Strategies
   without real planner support still get a visual sentiment arrow. */

// ── Sentiment + difficulty palettes (re-used by card UI) ──
export const SENTIMENT_META = {
  bullish:         { icon: "🐂",    label: "Bullish",            color: "#16a34a", bg: "#dcfce7" },
  bearish:         { icon: "🐻",    label: "Bearish",            color: "#dc2626", bg: "#fee2e2" },
  neutral:         { icon: "↔️",   label: "Neutral",            color: "#6b7280", bg: "#f3f4f6" },
  neutral_bearish: { icon: "🚫🐂", label: "Neutral/Bearish",    color: "#b45309", bg: "#fef3c7" },
  neutral_bullish: { icon: "🚫🐻", label: "Neutral/Bullish",    color: "#0369a1", bg: "#e0f2fe" },
  vol:             { icon: "💥",    label: "Volatility Play",    color: "#7c3aed", bg: "#ede9fe" },
  vol_bullish:     { icon: "💥🐂", label: "Bullish Volatility", color: "#7c3aed", bg: "#ede9fe" },
  vol_bearish:     { icon: "💥🐻", label: "Bearish Volatility", color: "#7c3aed", bg: "#ede9fe" },
};

export const DIFFICULTY_META = {
  beginner:     { label: "Beginner",     icon: "🟢", color: "#16a34a", bg: "#dcfce7" },
  intermediate: { label: "Intermediate", icon: "🟡", color: "#b45309", bg: "#fef3c7" },
  advanced:     { label: "Advanced",     icon: "🔴", color: "#dc2626", bg: "#fee2e2" },
};

export const CATEGORY_META = {
  BASIC:      { label: "Basic Directional",   icon: "📈" },
  VERTICAL:   { label: "Vertical Spreads",    icon: "⬍"  },
  NEUTRAL_VOL:{ label: "Neutral / Volatility",icon: "💥" },
  CONDOR:     { label: "Iron Condor Family",  icon: "🦅" },
  BUTTERFLY:  { label: "Butterflies",         icon: "🦋" },
  LADDER:     { label: "Ladders",             icon: "🪜" },
  RATIO:      { label: "Ratio Spreads",       icon: "⚖️"  },
  SPECIALIST: { label: "Specialist",          icon: "🎓" },
};

// ── Payoff primitives ────────────────────────────────────
// S = stock price at expiration, k = strike, qty = contracts (*100).
const callVal = (S, k) => Math.max(0, S - k);
const putVal  = (S, k) => Math.max(0, k - S);
const DOLLAR  = 100;   // contract multiplier

// Each strategy exposes a `payoff(S, ctx)` returning net dollar P/L per
// 1 contract at `S` on expiration, where `ctx` is { strikes:[], prem:{}, qty }.
// For strategies we don't interactively support yet, payoff returns a
// simple sentiment-only line to visualise the general shape.

// Rounded strike helper: snap to $5 above $100, else $1.
const roundStrike = (price, offset = 0) => {
  const inc = price > 100 ? 5 : 1;
  return Math.round((price + offset) / inc) * inc;
};

// Rough premium estimates for worked examples (simple volatility model —
// NOT Black-Scholes; this is just for the "here's how the math looks"
// card, real planner uses live quotes).
const atmPrem = (price) => Math.max(0.5, +(price * 0.02).toFixed(2));          // ~2% of spot
const otmPrem = (price, dist) => Math.max(0.2, +(atmPrem(price) - dist * 0.3).toFixed(2));

// ── THE 35 STRATEGIES ────────────────────────────────────
export const STRATEGIES_LIB = [
  // ══════ CATEGORY 1 — BASIC DIRECTIONAL ══════
  {
    id: "long_call", name: "Long Call", category: "BASIC",
    sentiment: "bullish", difficulty: "beginner", risk: "defined",
    plannerKind: "long_call", net: "debit",
    short: "Right to buy 100 shares at strike — max upside.",
    whatIs: "Buying a call gives you the right (but not obligation) to buy 100 shares of the underlying stock at the strike price before expiration. You pay a premium upfront; if the stock rises above strike+premium you profit unlimited. If it stays below, you lose only the premium.",
    whenToUse: "Strong bullish conviction — earnings plays, breakout setups, strong upside catalysts.",
    legs: [{ side: "buy", kind: "call", label: "1 ATM or slightly ITM call" }],
    maxProfit: "Unlimited 🤑",
    maxLoss:   "Premium paid",
    breakeven: "Strike + premium",
    pros: ["Limited risk — can only lose premium", "Unlimited upside", "High leverage on capital"],
    cons: ["Time decay works against you", "Needs significant move to profit", "High IV makes expensive"],
    example: (p) => {
      const k = roundStrike(p, p * 0.02);
      const prem = atmPrem(p);
      return {
        setup: `${p > 100 ? "NVDA" : "AAPL"} at $${p.toFixed(2)}. Buy $${k} call for $${prem.toFixed(2)} (45 DTE).`,
        cost:  `Cost = $${(prem * DOLLAR).toFixed(0)} per contract.`,
        win:   `If stock rises to $${(k + prem * 3).toFixed(2)}: profit = ${((k + prem * 3 - k - prem) * DOLLAR).toFixed(0)}.`,
        lose:  `If stock stays below $${k}: lose full premium ($${(prem * DOLLAR).toFixed(0)}).`,
      };
    },
    payoff: (S, { strikes, prem }) => (callVal(S, strikes[0]) - prem) * DOLLAR,
    defaultStrikes: (p) => [roundStrike(p, p * 0.02)],
    defaultPrem:    (p) => atmPrem(p),
  },
  {
    id: "short_call", name: "Short Call", category: "BASIC",
    sentiment: "neutral_bearish", difficulty: "advanced", risk: "undefined",
    plannerKind: null, net: "credit",
    short: "Sell a call — obligation to deliver shares at strike. UNLIMITED RISK.",
    whatIs: "Selling a naked call obligates you to sell 100 shares at strike if exercised. You collect premium up front. If the stock rises above strike, your losses grow without limit — shares can theoretically rise to any price.",
    whenToUse: "Strong bearish/neutral conviction on resistance. Institutional-only — requires massive margin.",
    legs: [{ side: "sell", kind: "call", label: "1 OTM call (naked)" }],
    maxProfit: "Premium received",
    maxLoss:   "UNLIMITED ⚠️",
    breakeven: "Strike + premium",
    pros: ["Immediate premium income", "Profits from time decay", "Wins 70%+ of the time if well-chosen"],
    cons: ["UNLIMITED upside loss", "Huge margin requirement", "One black-swan rally can wipe account"],
    example: (p) => {
      const k = roundStrike(p, p * 0.04);
      const prem = otmPrem(p, p * 0.04);
      return {
        setup: `Stock at $${p.toFixed(2)}. Sell $${k} call for $${prem.toFixed(2)}.`,
        cost:  `Receive $${(prem * DOLLAR).toFixed(0)} credit.`,
        win:   `Stock stays below $${k}: keep full $${(prem * DOLLAR).toFixed(0)}.`,
        lose:  `Stock rallies to $${(k * 1.1).toFixed(2)}: lose $${((k * 1.1 - k - prem) * DOLLAR).toFixed(0)}.`,
      };
    },
    payoff: (S, { strikes, prem }) => (prem - callVal(S, strikes[0])) * DOLLAR,
    defaultStrikes: (p) => [roundStrike(p, p * 0.04)],
    defaultPrem:    (p) => otmPrem(p, p * 0.04),
  },
  {
    id: "long_put", name: "Long Put", category: "BASIC",
    sentiment: "bearish", difficulty: "beginner", risk: "defined",
    plannerKind: "long_put", net: "debit",
    short: "Right to sell 100 shares at strike — profit on downside.",
    whatIs: "Buying a put gives you the right to sell 100 shares at the strike price before expiry. You pay premium upfront; profits grow as the stock falls. Max loss is just the premium.",
    whenToUse: "Strong bearish conviction, portfolio hedge, earnings downside plays.",
    legs: [{ side: "buy", kind: "put", label: "1 ATM or slightly ITM put" }],
    maxProfit: "Strike − premium (stock can fall to zero)",
    maxLoss:   "Premium paid",
    breakeven: "Strike − premium",
    pros: ["Limited risk", "Profits from downside moves", "Natural portfolio insurance"],
    cons: ["Time decay", "Needs big drop to offset premium", "IV skew makes puts expensive"],
    example: (p) => {
      const k = roundStrike(p, -p * 0.02);
      const prem = atmPrem(p);
      return {
        setup: `Stock at $${p.toFixed(2)}. Buy $${k} put for $${prem.toFixed(2)} (45 DTE).`,
        cost:  `Cost = $${(prem * DOLLAR).toFixed(0)}.`,
        win:   `Stock falls to $${(k - prem * 3).toFixed(2)}: profit ~$${((prem * 2) * DOLLAR).toFixed(0)}.`,
        lose:  `Stock stays above $${k}: lose full premium.`,
      };
    },
    payoff: (S, { strikes, prem }) => (putVal(S, strikes[0]) - prem) * DOLLAR,
    defaultStrikes: (p) => [roundStrike(p, -p * 0.02)],
    defaultPrem:    (p) => atmPrem(p),
  },
  {
    id: "short_put", name: "Short Put / Cash Secured Put", category: "BASIC",
    sentiment: "neutral_bullish", difficulty: "beginner", risk: "defined",
    plannerKind: null, net: "credit",
    short: "Sell a put — get paid to buy stock at strike if assigned. Also called Cash Secured Put.",
    whatIs: "Selling a put obligates you to buy 100 shares at strike if assigned. You collect premium. Ideal way to enter a stock at a discount or earn income on cash. When fully cash-collateralised (cash reserved to cover assignment), this is known as a Cash Secured Put.",
    whenToUse: "Bullish/neutral — willing to own the stock at strike.",
    legs: [{ side: "sell", kind: "put", label: "1 OTM put (cash-secured)" }],
    maxProfit: "Premium received",
    maxLoss:   "(Strike − premium) × 100",
    breakeven: "Strike − premium",
    pros: ["Get paid to wait for stock", "Profit from time decay", "Defined downside (stock to $0)"],
    cons: ["Ties up capital as collateral", "Full downside below strike", "Limited upside"],
    example: (p) => {
      const k = roundStrike(p, -p * 0.04);
      const prem = otmPrem(p, p * 0.04);
      return {
        setup: `Stock at $${p.toFixed(2)}. Sell $${k} put for $${prem.toFixed(2)}.`,
        cost:  `Receive $${(prem * DOLLAR).toFixed(0)} credit. Reserve $${(k * DOLLAR).toFixed(0)} collateral.`,
        win:   `Stock stays above $${k}: keep full credit.`,
        lose:  `Stock falls to $${(k * 0.9).toFixed(2)}: assigned at $${k}, effective cost $${(k - prem).toFixed(2)}.`,
      };
    },
    payoff: (S, { strikes, prem }) => (prem - putVal(S, strikes[0])) * DOLLAR,
    defaultStrikes: (p) => [roundStrike(p, -p * 0.04)],
    defaultPrem:    (p) => otmPrem(p, p * 0.04),
  },

  // ══════ CATEGORY 2 — VERTICAL SPREADS ══════
  {
    id: "call_debit_spread", name: "Call Debit Spread", category: "VERTICAL",
    sentiment: "bullish", difficulty: "beginner", risk: "defined",
    plannerKind: "Call Debit Spread", net: "debit",
    short: "Bullish spread — buy lower call, sell higher call. Capped upside + reduced cost.",
    whatIs: "Buy a lower-strike call and sell a higher-strike call at the same expiration. The short leg's premium offsets the long leg's cost. Max gain is the spread width minus debit; max loss is the debit.",
    whenToUse: "Moderately bullish — target price is between the two strikes.",
    legs: [
      { side: "buy",  kind: "call", label: "1 lower-strike call (closer to money)" },
      { side: "sell", kind: "call", label: "1 higher-strike call (OTM)" },
    ],
    maxProfit: "(Width − debit) × 100",
    maxLoss:   "Debit × 100",
    breakeven: "Lower strike + debit",
    pros: ["Lower cost than long call", "Defined max loss", "Profits in slow-grinding bull moves"],
    cons: ["Capped upside", "Needs move by expiration", "Early assignment risk on short leg"],
    example: (p) => {
      const lo = roundStrike(p, p * 0.01);
      const hi = lo + (p > 100 ? 5 : 1);
      const debit = +((hi - lo) * 0.45).toFixed(2);
      return {
        setup: `Stock at $${p.toFixed(2)}. Buy $${lo} call + sell $${hi} call.`,
        cost:  `Net debit = $${debit.toFixed(2)} ($${(debit * DOLLAR).toFixed(0)} per spread).`,
        win:   `Stock above $${hi} at expiry: max profit = $${((hi - lo - debit) * DOLLAR).toFixed(0)}.`,
        lose:  `Stock below $${lo}: lose full debit.`,
      };
    },
    payoff: (S, { strikes, prem }) =>
      (callVal(S, strikes[0]) - callVal(S, strikes[1]) - prem) * DOLLAR,
    defaultStrikes: (p) => { const lo = roundStrike(p, p * 0.01); return [lo, lo + (p > 100 ? 5 : 1)]; },
    defaultPrem:    (p) => +((p > 100 ? 5 : 1) * 0.45).toFixed(2),
  },
  {
    id: "call_credit_spread", name: "Call Credit Spread", category: "VERTICAL",
    sentiment: "neutral_bearish", difficulty: "beginner", risk: "defined",
    plannerKind: "Call Credit Spread", net: "credit",
    short: "Bearish/neutral income — sell lower call, buy higher call. Defined risk.",
    whatIs: "Sell a call and buy a higher-strike call. You collect credit up front. Profits if stock stays below the short strike. Max loss capped at the spread width minus credit.",
    whenToUse: "Bearish or neutral bias — stock expected to stay below resistance.",
    legs: [
      { side: "sell", kind: "call", label: "1 lower-strike call (closer to money)" },
      { side: "buy",  kind: "call", label: "1 higher-strike call (hedge)" },
    ],
    maxProfit: "Credit × 100",
    maxLoss:   "(Width − credit) × 100",
    breakeven: "Short strike + credit",
    pros: ["Immediate income", "Time decay works for you", "Defined max loss"],
    cons: ["Small max profit vs capital at risk", "Wider spreads risk early assignment"],
    example: (p) => {
      const lo = roundStrike(p, p * 0.02);
      const hi = lo + (p > 100 ? 5 : 1);
      const credit = +((hi - lo) * 0.30).toFixed(2);
      return {
        setup: `Stock at $${p.toFixed(2)}. Sell $${lo} call + buy $${hi} call.`,
        cost:  `Net credit = $${credit.toFixed(2)} ($${(credit * DOLLAR).toFixed(0)}).`,
        win:   `Stock below $${lo} at expiry: keep full credit.`,
        lose:  `Stock above $${hi}: lose $${((hi - lo - credit) * DOLLAR).toFixed(0)}.`,
      };
    },
    payoff: (S, { strikes, prem }) =>
      (prem - (callVal(S, strikes[0]) - callVal(S, strikes[1]))) * DOLLAR,
    defaultStrikes: (p) => { const lo = roundStrike(p, p * 0.02); return [lo, lo + (p > 100 ? 5 : 1)]; },
    defaultPrem:    (p) => +((p > 100 ? 5 : 1) * 0.30).toFixed(2),
  },
  {
    id: "put_debit_spread", name: "Put Debit Spread", category: "VERTICAL",
    sentiment: "bearish", difficulty: "beginner", risk: "defined",
    plannerKind: "Put Debit Spread", net: "debit",
    short: "Bearish spread — buy higher put, sell lower put. Defined risk + reduced cost.",
    whatIs: "Buy a higher-strike put and sell a lower-strike put, same expiration. The short leg offsets the long leg's cost. Profits when stock falls into the spread zone.",
    whenToUse: "Moderately bearish — downside target between the two strikes.",
    legs: [
      { side: "buy",  kind: "put", label: "1 higher-strike put" },
      { side: "sell", kind: "put", label: "1 lower-strike put (OTM)" },
    ],
    maxProfit: "(Width − debit) × 100",
    maxLoss:   "Debit × 100",
    breakeven: "Higher strike − debit",
    pros: ["Cheaper than long put", "Defined loss", "Still profits from downside"],
    cons: ["Capped profit", "Needs move by expiration"],
    example: (p) => {
      const hi = roundStrike(p, -p * 0.01);
      const lo = hi - (p > 100 ? 5 : 1);
      const debit = +((hi - lo) * 0.45).toFixed(2);
      return {
        setup: `Stock at $${p.toFixed(2)}. Buy $${hi} put + sell $${lo} put.`,
        cost:  `Net debit = $${debit.toFixed(2)}.`,
        win:   `Stock below $${lo}: max profit = $${((hi - lo - debit) * DOLLAR).toFixed(0)}.`,
        lose:  `Stock above $${hi}: lose full debit.`,
      };
    },
    payoff: (S, { strikes, prem }) =>
      (putVal(S, strikes[0]) - putVal(S, strikes[1]) - prem) * DOLLAR,
    defaultStrikes: (p) => { const hi = roundStrike(p, -p * 0.01); return [hi, hi - (p > 100 ? 5 : 1)]; },
    defaultPrem:    (p) => +((p > 100 ? 5 : 1) * 0.45).toFixed(2),
  },
  {
    id: "put_credit_spread", name: "Put Credit Spread", category: "VERTICAL",
    sentiment: "neutral_bullish", difficulty: "beginner", risk: "defined",
    plannerKind: "Put Credit Spread", net: "credit",
    short: "Bullish income — sell higher put, buy lower put. Defined risk credit spread.",
    whatIs: "Sell a put and buy a lower-strike put. Collect credit. Profits if stock stays above the short strike. Max loss capped at width minus credit.",
    whenToUse: "Bullish/neutral — stock expected to hold support.",
    legs: [
      { side: "sell", kind: "put", label: "1 higher-strike put (closer to money)" },
      { side: "buy",  kind: "put", label: "1 lower-strike put (hedge)" },
    ],
    maxProfit: "Credit × 100",
    maxLoss:   "(Width − credit) × 100",
    breakeven: "Short strike − credit",
    pros: ["Immediate income", "Time decay positive", "Defined max loss"],
    cons: ["Small reward vs risk", "Binary outcome near expiry"],
    example: (p) => {
      const hi = roundStrike(p, -p * 0.02);
      const lo = hi - (p > 100 ? 5 : 1);
      const credit = +((hi - lo) * 0.30).toFixed(2);
      return {
        setup: `Stock at $${p.toFixed(2)}. Sell $${hi} put + buy $${lo} put.`,
        cost:  `Net credit = $${credit.toFixed(2)}.`,
        win:   `Stock above $${hi}: keep full credit.`,
        lose:  `Stock below $${lo}: lose $${((hi - lo - credit) * DOLLAR).toFixed(0)}.`,
      };
    },
    payoff: (S, { strikes, prem }) =>
      (prem - (putVal(S, strikes[0]) - putVal(S, strikes[1]))) * DOLLAR,
    defaultStrikes: (p) => { const hi = roundStrike(p, -p * 0.02); return [hi, hi - (p > 100 ? 5 : 1)]; },
    defaultPrem:    (p) => +((p > 100 ? 5 : 1) * 0.30).toFixed(2),
  },

  // ══════ CATEGORY 3 — NEUTRAL / VOLATILITY ══════
  {
    id: "straddle", name: "Long Straddle", category: "NEUTRAL_VOL",
    sentiment: "vol", difficulty: "intermediate", risk: "defined",
    plannerKind: "long_straddle", net: "debit",
    short: "Buy ATM call + ATM put — profits on big move either direction.",
    whatIs: "Buy an ATM call and ATM put at the same strike and expiration. Profits from a large move in either direction. Ideal for high-expected-volatility events.",
    whenToUse: "Earnings, FDA decisions, Fed announcements — large move expected, direction unknown.",
    legs: [
      { side: "buy", kind: "call", label: "1 ATM call" },
      { side: "buy", kind: "put",  label: "1 ATM put (same strike)" },
    ],
    maxProfit: "Unlimited 🤑",
    maxLoss:   "Both premiums paid",
    breakeven: "Strike ± total premium (two breakevens)",
    pros: ["Directionally agnostic", "Unlimited upside", "Defined max loss"],
    cons: ["Expensive — 2x premiums", "IV crush after event kills value", "Large move needed"],
    example: (p) => {
      const k = roundStrike(p);
      const cP = atmPrem(p), pP = atmPrem(p) * 0.9;
      const total = +(cP + pP).toFixed(2);
      return {
        setup: `Stock at $${p.toFixed(2)}. Buy $${k} call for $${cP.toFixed(2)} + buy $${k} put for $${pP.toFixed(2)}.`,
        cost:  `Total cost = $${total.toFixed(2)} ($${(total * DOLLAR).toFixed(0)}).`,
        win:   `Stock moves > $${total.toFixed(2)} either direction: profitable. Unlimited upside.`,
        lose:  `Stock pins near $${k}: lose most of premium.`,
      };
    },
    payoff: (S, { strikes, prem }) =>
      (callVal(S, strikes[0]) + putVal(S, strikes[0]) - prem) * DOLLAR,
    defaultStrikes: (p) => [roundStrike(p)],
    defaultPrem:    (p) => +(atmPrem(p) * 1.9).toFixed(2),
  },
  {
    id: "short_straddle", name: "Short Straddle", category: "NEUTRAL_VOL",
    sentiment: "neutral", difficulty: "advanced", risk: "undefined",
    plannerKind: null, net: "credit",
    short: "Sell ATM call + ATM put — profits if stock pins. UNLIMITED LOSS.",
    whatIs: "Sell both an ATM call and ATM put. You collect both premiums. Maximum profit at strike; losses grow without limit on any significant move.",
    whenToUse: "Low-volatility environment — stock expected to pin to strike.",
    legs: [
      { side: "sell", kind: "call", label: "1 ATM call" },
      { side: "sell", kind: "put",  label: "1 ATM put (same strike)" },
    ],
    maxProfit: "Both premiums",
    maxLoss:   "UNLIMITED ⚠️",
    breakeven: "Strike ± total credit",
    pros: ["Huge premium collection", "Wins if stock pins"],
    cons: ["UNLIMITED risk both sides", "Massive margin", "One gap ruins year of profits"],
    example: (p) => {
      const k = roundStrike(p);
      const cP = atmPrem(p), pP = atmPrem(p) * 0.9;
      return {
        setup: `Stock at $${p.toFixed(2)}. Sell $${k} call + sell $${k} put.`,
        cost:  `Receive $${((cP + pP) * DOLLAR).toFixed(0)} credit.`,
        win:   `Stock pins near $${k}: keep most of credit.`,
        lose:  `Stock moves to $${(k * 1.15).toFixed(2)}: lose ~$${((k * 0.15 - cP - pP) * DOLLAR).toFixed(0)}.`,
      };
    },
    payoff: (S, { strikes, prem }) =>
      (prem - callVal(S, strikes[0]) - putVal(S, strikes[0])) * DOLLAR,
    defaultStrikes: (p) => [roundStrike(p)],
    defaultPrem:    (p) => +(atmPrem(p) * 1.9).toFixed(2),
  },
  {
    id: "strangle", name: "Long Strangle", category: "NEUTRAL_VOL",
    sentiment: "vol", difficulty: "intermediate", risk: "defined",
    plannerKind: "long_strangle", net: "debit",
    short: "Buy OTM call + OTM put — cheaper than straddle, needs bigger move.",
    whatIs: "Buy an OTM call and an OTM put at different strikes. Cheaper than a straddle but requires a bigger move to become profitable.",
    whenToUse: "High-volatility event where you can afford lower cost + bigger-move requirement.",
    legs: [
      { side: "buy", kind: "call", label: "1 OTM call" },
      { side: "buy", kind: "put",  label: "1 OTM put (lower strike)" },
    ],
    maxProfit: "Unlimited 🤑",
    maxLoss:   "Both premiums paid",
    breakeven: "Call strike + premium / Put strike − premium",
    pros: ["Cheaper than straddle", "Unlimited upside", "Defined max loss"],
    cons: ["Needs big move", "IV crush hurts", "Two-sided breakeven"],
    example: (p) => {
      const kc = roundStrike(p, p * 0.02);
      const kp = roundStrike(p, -p * 0.02);
      const cP = otmPrem(p, p * 0.02), pP = otmPrem(p, p * 0.02);
      const total = +(cP + pP).toFixed(2);
      return {
        setup: `Stock at $${p.toFixed(2)}. Buy $${kc} call + buy $${kp} put.`,
        cost:  `Total = $${(total * DOLLAR).toFixed(0)}.`,
        win:   `Stock > $${(kc + total).toFixed(2)} or < $${(kp - total).toFixed(2)}: profitable.`,
        lose:  `Stock between strikes: lose up to $${(total * DOLLAR).toFixed(0)}.`,
      };
    },
    payoff: (S, { strikes, prem }) =>
      (callVal(S, strikes[0]) + putVal(S, strikes[1]) - prem) * DOLLAR,
    defaultStrikes: (p) => [roundStrike(p, p * 0.02), roundStrike(p, -p * 0.02)],
    defaultPrem:    (p) => +(otmPrem(p, p * 0.02) * 2).toFixed(2),
  },
  {
    id: "short_strangle", name: "Short Strangle", category: "NEUTRAL_VOL",
    sentiment: "neutral", difficulty: "advanced", risk: "undefined",
    plannerKind: null, net: "credit",
    short: "Sell OTM call + OTM put — profits in range. UNLIMITED LOSS.",
    whatIs: "Sell OTM call and OTM put at different strikes. Collect both premiums. Profits if stock stays inside the range; losses are unlimited outside.",
    whenToUse: "Range-bound low-volatility markets.",
    legs: [
      { side: "sell", kind: "call", label: "1 OTM call" },
      { side: "sell", kind: "put",  label: "1 OTM put" },
    ],
    maxProfit: "Both premiums",
    maxLoss:   "UNLIMITED ⚠️",
    breakeven: "Upper = call strike + credit, Lower = put strike − credit",
    pros: ["Wider profit zone than short straddle", "Premium income"],
    cons: ["UNLIMITED risk both sides", "Margin intensive", "Gaps are fatal"],
    example: (p) => {
      const kc = roundStrike(p, p * 0.04);
      const kp = roundStrike(p, -p * 0.04);
      return {
        setup: `Stock at $${p.toFixed(2)}. Sell $${kc} call + sell $${kp} put.`,
        cost:  `Collect both OTM premiums.`,
        win:   `Stock stays between $${kp}–$${kc}: keep full credit.`,
        lose:  `Breakout either side: unlimited loss.`,
      };
    },
    payoff: (S, { strikes, prem }) =>
      (prem - callVal(S, strikes[0]) - putVal(S, strikes[1])) * DOLLAR,
    defaultStrikes: (p) => [roundStrike(p, p * 0.04), roundStrike(p, -p * 0.04)],
    defaultPrem:    (p) => +(otmPrem(p, p * 0.04) * 2).toFixed(2),
  },

  // ══════ CATEGORY 4 — IRON CONDOR FAMILY ══════
  {
    id: "iron_condor", name: "Iron Condor", category: "CONDOR",
    sentiment: "neutral", difficulty: "beginner", risk: "defined",
    plannerKind: "Iron Condor", net: "credit",
    short: "Sell put spread + sell call spread — income in a range.",
    whatIs: "Four-leg neutral strategy: sell an OTM put spread + sell an OTM call spread. Defined risk on both sides. Profits if stock stays inside the short strikes.",
    whenToUse: "Range-bound market with declining IV.",
    legs: [
      { side: "buy",  kind: "put",  label: "Lower-strike put (long wing)"  },
      { side: "sell", kind: "put",  label: "Higher-strike put (short)"      },
      { side: "sell", kind: "call", label: "Lower-strike call (short)"      },
      { side: "buy",  kind: "call", label: "Higher-strike call (long wing)" },
    ],
    maxProfit: "Net credit",
    maxLoss:   "Widest wing − credit",
    breakeven: "Short put − credit / Short call + credit",
    pros: ["Defined risk both sides", "High probability of profit", "Benefits from time decay"],
    cons: ["Small reward vs risk", "Losses mount fast on breakouts"],
    example: (p) => {
      const w = p > 100 ? 5 : 1;
      const cs = roundStrike(p, p * 0.03);
      const cb = cs + w;
      const ps = roundStrike(p, -p * 0.03);
      const pb = ps - w;
      return {
        setup: `Stock at $${p.toFixed(2)}. Sell $${ps}p / buy $${pb}p + sell $${cs}c / buy $${cb}c.`,
        cost:  `Net credit ~$${(w * 0.3).toFixed(2)}.`,
        win:   `Stock stays between $${ps}–$${cs}: keep full credit.`,
        lose:  `Stock breaks either wing: capped loss $${((w - w * 0.3) * DOLLAR).toFixed(0)}.`,
      };
    },
    payoff: (S, { strikes, prem }) => {
      // strikes = [putBuy, putSell, callSell, callBuy]
      const putDebt  = Math.max(0, putVal(S, strikes[1])  - putVal(S, strikes[0]));
      const callDebt = Math.max(0, callVal(S, strikes[2]) - callVal(S, strikes[3]));
      return (prem - putDebt - callDebt) * DOLLAR;
    },
    defaultStrikes: (p) => {
      const w = p > 100 ? 5 : 1;
      const cs = roundStrike(p, p * 0.03);
      const ps = roundStrike(p, -p * 0.03);
      return [ps - w, ps, cs, cs + w];
    },
    defaultPrem: (p) => +(((p > 100 ? 5 : 1) * 0.3)).toFixed(2),
  },
  {
    id: "short_iron_condor", name: "Short Iron Condor", category: "CONDOR",
    sentiment: "vol", difficulty: "intermediate", risk: "defined",
    plannerKind: null, net: "debit",
    short: "Reverse iron condor — profits on breakouts from range.",
    whatIs: "Buy call spread + buy put spread. Pay debit; profit if stock breaks either wing.",
    whenToUse: "Expecting a breakout in either direction with defined cost.",
    legs: [
      { side: "sell", kind: "put",  label: "Lower-strike put"  },
      { side: "buy",  kind: "put",  label: "Higher-strike put" },
      { side: "buy",  kind: "call", label: "Lower-strike call" },
      { side: "sell", kind: "call", label: "Higher-strike call" },
    ],
    maxProfit: "(Width − debit) × 100",
    maxLoss:   "Debit × 100",
    breakeven: "Short put − debit / Short call + debit",
    pros: ["Defined risk + breakout exposure", "Cheaper than strangle"],
    cons: ["Needs big move", "Capped upside"],
    example: (p) => ({
      setup: `Stock at $${p.toFixed(2)}. Reverse iron condor around spot.`,
      cost:  `Net debit — pay for breakout exposure.`,
      win:   `Stock breaks either wing: max profit = width − debit.`,
      lose:  `Stock stays range-bound: lose full debit.`,
    }),
    payoff: null,
  },
  {
    id: "call_condor", name: "Call Condor", category: "CONDOR",
    sentiment: "neutral", difficulty: "advanced", risk: "defined",
    plannerKind: null, net: "debit",
    short: "Four calls — profits if stock lands between middle strikes.",
    whatIs: "Buy low call + sell two middle calls + buy high call. Max profit is when stock expires between the two middle strikes.",
    whenToUse: "Precise range target with low cost.",
    legs: [
      { side: "buy",  kind: "call", label: "Low-strike call" },
      { side: "sell", kind: "call", label: "Mid-low call" },
      { side: "sell", kind: "call", label: "Mid-high call" },
      { side: "buy",  kind: "call", label: "High-strike call" },
    ],
    maxProfit: "Middle width − debit",
    maxLoss:   "Debit paid",
    breakeven: "Two breakevens based on outer strikes",
    pros: ["Low cost", "Defined risk"],
    cons: ["Narrow profit zone", "Hard to manage"],
    example: (p) => ({
      setup: `Stock at $${p.toFixed(2)}. Call condor around spot.`,
      cost:  `Small debit.`,
      win:   `Stock between middle strikes: max profit.`,
      lose:  `Stock outside outer wings: lose debit.`,
    }),
    payoff: null,
  },
  {
    id: "short_call_condor", name: "Short Call Condor", category: "CONDOR",
    sentiment: "vol", difficulty: "advanced", risk: "defined",
    plannerKind: null, net: "credit",
    short: "Reverse call condor — profits on move away from middle strikes.",
    whatIs: "Sell outer calls + buy middle calls. Collect credit; profits if stock moves outside the middle zone.",
    whenToUse: "Expecting move away from current price.",
    legs: [
      { side: "sell", kind: "call", label: "Low-strike call" },
      { side: "buy",  kind: "call", label: "Mid-low call" },
      { side: "buy",  kind: "call", label: "Mid-high call" },
      { side: "sell", kind: "call", label: "High-strike call" },
    ],
    maxProfit: "Credit received",
    maxLoss:   "Width − credit",
    breakeven: "Two — outer",
    pros: ["Defined risk", "Income from vol"],
    cons: ["Small edge", "Complex"],
    example: (p) => ({
      setup: `Stock at $${p.toFixed(2)}. Reverse call condor.`,
      cost:  `Small credit.`,
      win:   `Stock moves outside middle: keep credit.`,
      lose:  `Stock lands mid: pay max loss.`,
    }),
    payoff: null,
  },
  {
    id: "put_condor", name: "Put Condor", category: "CONDOR",
    sentiment: "neutral", difficulty: "advanced", risk: "defined",
    plannerKind: null, net: "debit",
    short: "Put version of call condor — same neutral payoff.",
    whatIs: "Buy low put + sell two middle puts + buy high put. Same payoff as call condor using puts.",
    whenToUse: "Same as call condor — neutral narrow range.",
    legs: [
      { side: "buy",  kind: "put", label: "Low-strike put" },
      { side: "sell", kind: "put", label: "Mid-low put" },
      { side: "sell", kind: "put", label: "Mid-high put" },
      { side: "buy",  kind: "put", label: "High-strike put" },
    ],
    maxProfit: "Middle width − debit",
    maxLoss:   "Debit",
    breakeven: "Two — outer",
    pros: ["Same payoff as call condor"],
    cons: ["Put-specific skew"],
    example: (p) => ({
      setup: `Stock at $${p.toFixed(2)}. Put condor around spot.`,
      cost:  `Small debit.`,
      win:   `Stock between middle strikes.`,
      lose:  `Outside outer wings.`,
    }),
    payoff: null,
  },
  {
    id: "short_put_condor", name: "Short Put Condor", category: "CONDOR",
    sentiment: "vol", difficulty: "advanced", risk: "defined",
    plannerKind: null, net: "credit",
    short: "Reverse put condor — profits on move.",
    whatIs: "Mirror of short call condor using puts.",
    whenToUse: "Expected move out of range.",
    legs: [
      { side: "sell", kind: "put", label: "Low put" },
      { side: "buy",  kind: "put", label: "Mid-low put" },
      { side: "buy",  kind: "put", label: "Mid-high put" },
      { side: "sell", kind: "put", label: "High put" },
    ],
    maxProfit: "Credit",
    maxLoss:   "Width − credit",
    breakeven: "Two outer",
    pros: ["Defined risk"], cons: ["Small edge"],
    example: (p) => ({ setup: `Stock at $${p.toFixed(2)}.`, cost: "Credit", win: "Move out of range", lose: "Pin mid" }),
    payoff: null,
  },

  // ══════ CATEGORY 5 — BUTTERFLIES ══════
  {
    id: "call_butterfly", name: "Call Butterfly", category: "BUTTERFLY",
    sentiment: "neutral", difficulty: "intermediate", risk: "defined",
    plannerKind: null, net: "debit",
    short: "Buy 1 low + sell 2 middle + buy 1 high — pin trade.",
    whatIs: "Three-strike structure: long the wings, short 2× the middle. Max profit if stock lands exactly at middle strike at expiration.",
    whenToUse: "Precise pin target (earnings mean-reversion, OPEX pin).",
    legs: [
      { side: "buy",  kind: "call", label: "1 low-strike call" },
      { side: "sell", kind: "call", label: "2 middle-strike calls" },
      { side: "buy",  kind: "call", label: "1 high-strike call" },
    ],
    maxProfit: "Wing width − debit",
    maxLoss:   "Debit",
    breakeven: "Low + debit / High − debit",
    pros: ["Very high R:R at pin", "Low cost"],
    cons: ["Tiny profit zone", "Needs exact pin"],
    example: (p) => {
      const k = roundStrike(p);
      const w = p > 100 ? 5 : 1;
      return {
        setup: `Stock at $${p.toFixed(2)}. Buy $${k - w} call + sell 2× $${k} call + buy $${k + w} call.`,
        cost:  `Small debit ~$${(w * 0.15).toFixed(2)}.`,
        win:   `Stock pins at $${k}: max profit = $${((w - w * 0.15) * DOLLAR).toFixed(0)}.`,
        lose:  `Stock outside wings: lose debit.`,
      };
    },
    payoff: null,
  },
  {
    id: "put_butterfly", name: "Put Butterfly", category: "BUTTERFLY",
    sentiment: "neutral", difficulty: "intermediate", risk: "defined",
    plannerKind: null, net: "debit",
    short: "Put version of call butterfly — same neutral pin payoff.",
    whatIs: "1 low put + 2 middle puts sold + 1 high put. Same payoff shape as call butterfly.",
    whenToUse: "Same — precise pin.",
    legs: [
      { side: "buy", kind: "put", label: "1 low-strike put" },
      { side: "sell", kind: "put", label: "2 middle puts" },
      { side: "buy", kind: "put", label: "1 high-strike put" },
    ],
    maxProfit: "Wing width − debit",
    maxLoss:   "Debit",
    breakeven: "Two",
    pros: ["Same R:R as call butterfly"], cons: ["Needs exact pin"],
    example: (p) => ({ setup: `Stock at $${p.toFixed(2)}.`, cost: "Small debit", win: "Pin at middle", lose: "Outside wings" }),
    payoff: null,
  },
  {
    id: "short_call_butterfly", name: "Short Call Butterfly", category: "BUTTERFLY",
    sentiment: "vol", difficulty: "advanced", risk: "defined",
    plannerKind: null, net: "credit",
    short: "Reverse — profits on move away from middle.",
    whatIs: "Sell the wings, buy 2× middle. Profits if stock moves far from middle.",
    whenToUse: "Expecting breakout from current price.",
    legs: [
      { side: "sell", kind: "call", label: "Low call" },
      { side: "buy",  kind: "call", label: "2 middle calls" },
      { side: "sell", kind: "call", label: "High call" },
    ],
    maxProfit: "Credit",
    maxLoss:   "Wing − credit",
    breakeven: "Two",
    pros: ["Defined risk vol play"], cons: ["Needs big move"],
    example: (p) => ({ setup: `Stock at $${p.toFixed(2)}.`, cost: "Small credit", win: "Move outside wings", lose: "Pin at middle" }),
    payoff: null,
  },
  {
    id: "short_put_butterfly", name: "Short Put Butterfly", category: "BUTTERFLY",
    sentiment: "vol", difficulty: "advanced", risk: "defined",
    plannerKind: null, net: "credit",
    short: "Put version of short call butterfly.",
    whatIs: "Mirror using puts. Profits on move away from middle.",
    whenToUse: "Breakout expected.",
    legs: [
      { side: "sell", kind: "put", label: "Low put" },
      { side: "buy",  kind: "put", label: "2 middle puts" },
      { side: "sell", kind: "put", label: "High put" },
    ],
    maxProfit: "Credit", maxLoss: "Wing − credit", breakeven: "Two",
    pros: ["Defined risk"], cons: ["Needs big move"],
    example: (p) => ({ setup: `Stock at $${p.toFixed(2)}.`, cost: "Small credit", win: "Move away from mid", lose: "Pin at mid" }),
    payoff: null,
  },

  // ══════ CATEGORY 6 — LADDERS ══════
  {
    id: "call_ladder", name: "Call Ladder", category: "LADDER",
    sentiment: "bullish", difficulty: "advanced", risk: "undefined",
    plannerKind: null, net: "mixed",
    short: "Buy 1 + sell 2 higher calls — cheap bull with upside blowup risk.",
    whatIs: "Buy 1 call + sell 1 call at higher strike + sell another at even higher strike. Reduces cost but exposes to UNLIMITED loss above highest strike.",
    whenToUse: "Moderately bullish + want low cost + accept upside tail risk.",
    legs: [
      { side: "buy",  kind: "call", label: "1 lower-strike call" },
      { side: "sell", kind: "call", label: "1 middle call" },
      { side: "sell", kind: "call", label: "1 higher call" },
    ],
    maxProfit: "Limited (between middle + high)",
    maxLoss:   "UNLIMITED above high ⚠️",
    breakeven: "Two breakevens",
    pros: ["Very cheap entry", "Profits in grinding uptrend"],
    cons: ["UNLIMITED upside risk", "Complex management"],
    example: (p) => ({ setup: `Stock at $${p.toFixed(2)}. Call ladder.`, cost: "Small debit/credit", win: "Stock between middle + high", lose: "Stock explodes above high" }),
    payoff: null,
  },
  {
    id: "short_call_ladder", name: "Short Call Ladder", category: "LADDER",
    sentiment: "vol", difficulty: "advanced", risk: "defined",
    plannerKind: null, net: "credit",
    short: "Reverse call ladder — profits on explosive upside.",
    whatIs: "Sell 1 call + buy 2 higher calls. Profits if stock explodes past highest strike.",
    whenToUse: "Expecting explosive bullish move.",
    legs: [
      { side: "sell", kind: "call", label: "1 low call" },
      { side: "buy",  kind: "call", label: "2 higher calls" },
    ],
    maxProfit: "Unlimited 🤑 upside",
    maxLoss:   "Limited middle loss",
    breakeven: "Two",
    pros: ["Defined loss", "Unlimited upside"],
    cons: ["Needs big move"],
    example: (p) => ({ setup: `Stock at $${p.toFixed(2)}.`, cost: "Credit", win: "Explosive upside", lose: "Pin middle" }),
    payoff: null,
  },
  {
    id: "put_ladder", name: "Put Ladder", category: "LADDER",
    sentiment: "bearish", difficulty: "advanced", risk: "undefined",
    plannerKind: null, net: "mixed",
    short: "Buy 1 + sell 2 lower puts — cheap bear with downside blowup risk.",
    whatIs: "Buy 1 put + sell 2 lower puts. Cheap bearish but UNLIMITED loss below lowest strike.",
    whenToUse: "Moderately bearish + cheap entry + accept downside tail.",
    legs: [
      { side: "buy",  kind: "put", label: "1 higher put" },
      { side: "sell", kind: "put", label: "1 middle put" },
      { side: "sell", kind: "put", label: "1 lower put" },
    ],
    maxProfit: "Limited (between lower and middle)",
    maxLoss:   "UNLIMITED below low ⚠️",
    breakeven: "Two",
    pros: ["Cheap bearish exposure"],
    cons: ["UNLIMITED downside risk"],
    example: (p) => ({ setup: `Stock at $${p.toFixed(2)}. Put ladder.`, cost: "Small debit/credit", win: "Stock between lower + middle", lose: "Crash below low" }),
    payoff: null,
  },
  {
    id: "short_put_ladder", name: "Short Put Ladder", category: "LADDER",
    sentiment: "vol", difficulty: "advanced", risk: "defined",
    plannerKind: null, net: "credit",
    short: "Reverse put ladder — profits on explosive downside.",
    whatIs: "Sell 1 put + buy 2 lower puts. Profits on big downside crash.",
    whenToUse: "Expecting explosive bearish move.",
    legs: [
      { side: "sell", kind: "put", label: "1 high put" },
      { side: "buy",  kind: "put", label: "2 lower puts" },
    ],
    maxProfit: "Unlimited 🤑 downside",
    maxLoss:   "Limited middle loss",
    breakeven: "Two",
    pros: ["Defined loss", "Big downside win"],
    cons: ["Needs big drop"],
    example: (p) => ({ setup: `Stock at $${p.toFixed(2)}.`, cost: "Credit", win: "Crash downside", lose: "Pin middle" }),
    payoff: null,
  },

  // ══════ CATEGORY 7 — RATIO SPREADS ══════
  {
    id: "call_ratio_spread", name: "Call Ratio Spread", category: "RATIO",
    sentiment: "bullish", difficulty: "advanced", risk: "undefined",
    plannerKind: null, net: "credit",
    short: "Buy 1 + sell 2 higher calls — moderate bull, UNLIMITED upside risk.",
    whatIs: "Buy 1 call + sell 2 higher-strike calls. Credit up front. Max profit at the short strike; UNLIMITED loss beyond.",
    whenToUse: "Moderately bullish with a precise ceiling in mind.",
    legs: [
      { side: "buy",  kind: "call", label: "1 lower call" },
      { side: "sell", kind: "call", label: "2 higher calls" },
    ],
    maxProfit: "Spread width + credit",
    maxLoss:   "UNLIMITED ⚠️",
    breakeven: "Short strike + width",
    pros: ["Positive credit", "Profits in moderate rally"],
    cons: ["UNLIMITED upside risk", "Margin intensive"],
    example: (p) => ({ setup: `Stock at $${p.toFixed(2)}. Call ratio spread.`, cost: "Small credit", win: "Stock hits short strike exactly", lose: "Stock explodes above" }),
    payoff: null,
  },
  {
    id: "put_ratio_spread", name: "Put Ratio Spread", category: "RATIO",
    sentiment: "bearish", difficulty: "advanced", risk: "undefined",
    plannerKind: null, net: "credit",
    short: "Buy 1 + sell 2 lower puts — moderate bear, UNLIMITED downside risk.",
    whatIs: "Buy 1 put + sell 2 lower puts. Credit up front; profits on moderate downside but UNLIMITED loss on crash.",
    whenToUse: "Moderately bearish with known floor.",
    legs: [
      { side: "buy",  kind: "put", label: "1 higher put" },
      { side: "sell", kind: "put", label: "2 lower puts" },
    ],
    maxProfit: "Spread width + credit",
    maxLoss:   "UNLIMITED ⚠️",
    breakeven: "Short strike − width",
    pros: ["Positive credit", "Moderate drop profits"],
    cons: ["UNLIMITED downside risk"],
    example: (p) => ({ setup: `Stock at $${p.toFixed(2)}.`, cost: "Small credit", win: "Stock hits short strike", lose: "Stock crashes below" }),
    payoff: null,
  },
  {
    id: "call_backspread", name: "Call Backspread", category: "RATIO",
    sentiment: "vol_bullish", difficulty: "advanced", risk: "defined",
    plannerKind: null, net: "credit",
    short: "Sell 1 + buy 2 higher calls — bullish volatility play.",
    whatIs: "Sell 1 call + buy 2 higher-strike calls. Net credit (usually). Profits explosively on big rally.",
    whenToUse: "Expecting explosive bullish move.",
    legs: [
      { side: "sell", kind: "call", label: "1 low call" },
      { side: "buy",  kind: "call", label: "2 higher calls" },
    ],
    maxProfit: "Unlimited 🤑",
    maxLoss:   "Limited (middle zone)",
    breakeven: "Two breakevens",
    pros: ["Unlimited upside", "Defined mid loss", "Possible credit"],
    cons: ["Loses in sideways", "Time decay hurts"],
    example: (p) => ({ setup: `Stock at $${p.toFixed(2)}.`, cost: "Credit", win: "Explosive upside", lose: "Pin middle" }),
    payoff: null,
  },
  {
    id: "put_backspread", name: "Put Backspread", category: "RATIO",
    sentiment: "vol_bearish", difficulty: "advanced", risk: "defined",
    plannerKind: null, net: "credit",
    short: "Sell 1 + buy 2 lower puts — bearish volatility play.",
    whatIs: "Sell 1 put + buy 2 lower puts. Profits on big crash.",
    whenToUse: "Expecting explosive bearish move.",
    legs: [
      { side: "sell", kind: "put", label: "1 high put" },
      { side: "buy",  kind: "put", label: "2 lower puts" },
    ],
    maxProfit: "Large 🤑 (stock → 0)",
    maxLoss:   "Limited middle",
    breakeven: "Two",
    pros: ["Big downside payoff", "Defined mid loss"],
    cons: ["Time decay hurts"],
    example: (p) => ({ setup: `Stock at $${p.toFixed(2)}.`, cost: "Credit", win: "Crash", lose: "Pin middle" }),
    payoff: null,
  },

  // ══════ CATEGORY 8 — SPECIALIST ══════
  {
    id: "strap", name: "Strap", category: "SPECIALIST",
    sentiment: "vol_bullish", difficulty: "intermediate", risk: "defined",
    plannerKind: null, net: "debit",
    short: "Buy 2 calls + 1 put — bullish straddle.",
    whatIs: "Buy 2 ATM calls + 1 ATM put. Like a straddle but with bullish tilt — profits faster on upside.",
    whenToUse: "Big move expected, directional bias upside.",
    legs: [
      { side: "buy", kind: "call", label: "2 ATM calls" },
      { side: "buy", kind: "put",  label: "1 ATM put" },
    ],
    maxProfit: "Unlimited 🤑 (upside 2x)",
    maxLoss:   "Total premium",
    breakeven: "Two breakevens",
    pros: ["Unlimited upside doubled", "Defined loss"],
    cons: ["Expensive — 3 premiums", "IV crush"],
    example: (p) => ({ setup: `Stock at $${p.toFixed(2)}. Buy 2× ATM call + 1× ATM put.`, cost: "~3× ATM premium", win: "Big upside move (2× speed)", lose: "Pin at strike" }),
    payoff: null,
  },
  {
    id: "strip", name: "Strip", category: "SPECIALIST",
    sentiment: "vol_bearish", difficulty: "intermediate", risk: "defined",
    plannerKind: null, net: "debit",
    short: "Buy 1 call + 2 puts — bearish straddle.",
    whatIs: "Buy 1 ATM call + 2 ATM puts. Like straddle with bearish tilt — profits faster on downside.",
    whenToUse: "Big move expected, bearish bias.",
    legs: [
      { side: "buy", kind: "call", label: "1 ATM call" },
      { side: "buy", kind: "put",  label: "2 ATM puts" },
    ],
    maxProfit: "Large (downside 2x)",
    maxLoss:   "Total premium",
    breakeven: "Two",
    pros: ["Doubled downside payoff"],
    cons: ["Expensive", "IV crush"],
    example: (p) => ({ setup: `Stock at $${p.toFixed(2)}. Buy 1× ATM call + 2× ATM put.`, cost: "~3× ATM", win: "Big drop (2× speed)", lose: "Pin at strike" }),
    payoff: null,
  },
  {
    id: "jade_lizard", name: "Jade Lizard", category: "SPECIALIST",
    sentiment: "neutral_bullish", difficulty: "advanced", risk: "defined",
    plannerKind: null, net: "credit",
    short: "Sell OTM put + sell OTM call spread — no upside risk.",
    whatIs: "Sell OTM put + sell a narrow OTM call credit spread. If credit received > call spread width, there is NO upside risk. Downside limited by put strike.",
    whenToUse: "Slightly bullish to neutral with known support floor.",
    legs: [
      { side: "sell", kind: "put",  label: "1 OTM put" },
      { side: "sell", kind: "call", label: "1 OTM call" },
      { side: "buy",  kind: "call", label: "1 further-OTM call (hedge)" },
    ],
    maxProfit: "Credit received",
    maxLoss:   "Put strike − credit",
    breakeven: "Put strike − credit",
    pros: ["No upside risk (if credit > spread width)", "High income"],
    cons: ["Full downside below put strike"],
    example: (p) => {
      const ps = roundStrike(p, -p * 0.03);
      const cs = roundStrike(p, p * 0.02);
      const cb = cs + (p > 100 ? 5 : 1);
      return {
        setup: `Stock at $${p.toFixed(2)}. Sell $${ps}p + sell $${cs}c + buy $${cb}c.`,
        cost:  `Net credit (arranged so credit > call spread width).`,
        win:   `Stock above $${ps}: keep full credit. No upside loss possible.`,
        lose:  `Stock below $${ps}: assigned at put strike.`,
      };
    },
    payoff: null,
  },
  {
    id: "synthetic_call", name: "Synthetic Call", category: "SPECIALIST",
    sentiment: "bullish", difficulty: "intermediate", risk: "defined",
    plannerKind: null, net: "mixed",
    short: "Long stock + long put = same payoff as long call.",
    whatIs: "Own 100 shares + buy a put. Same P/L profile as a long call but you actually hold the stock.",
    whenToUse: "Protect existing stock position from downside while keeping upside exposure.",
    legs: [
      { side: "long", kind: "stock", label: "100 shares of underlying" },
      { side: "buy",  kind: "put",   label: "1 ATM put (insurance)" },
    ],
    maxProfit: "Unlimited 🤑",
    maxLoss:   "Stock loss to put strike − put premium",
    breakeven: "Purchase price + put premium",
    pros: ["Unlimited upside", "Downside capped", "Simpler psychology than options-only"],
    cons: ["Premium cost", "Ties up full stock capital"],
    example: (p) => ({
      setup: `Own 100 shares at $${p.toFixed(2)} + buy $${roundStrike(p)} put for $${atmPrem(p).toFixed(2)}.`,
      cost:  `Put costs $${(atmPrem(p) * DOLLAR).toFixed(0)}.`,
      win:   `Stock rises — unlimited upside.`,
      lose:  `Stock falls below put strike — losses capped at (put strike − premium).`,
    }),
    payoff: null,
  },
  {
    id: "calendar_call", name: "Calendar Call Spread", category: "SPECIALIST",
    sentiment: "neutral", difficulty: "advanced", risk: "defined",
    plannerKind: null, net: "debit",
    short: "Sell short-term call + buy longer-term call at same strike.",
    whatIs: "Sell near-dated call + buy far-dated call at same strike. Profits from faster time decay on short leg while long leg retains value.",
    whenToUse: "Neutral-to-slightly-bullish + expecting low volatility near-term.",
    legs: [
      { side: "sell", kind: "call", label: "Short-dated call" },
      { side: "buy",  kind: "call", label: "Long-dated call (same strike)" },
    ],
    maxProfit: "Limited — at strike near short expiry",
    maxLoss:   "Debit paid",
    breakeven: "Varies by time structure",
    pros: ["Positive theta on short leg", "Defined risk"],
    cons: ["Vol movements unpredictable", "Complex management"],
    example: (p) => ({ setup: `Stock at $${p.toFixed(2)}. Sell 30-day call, buy 60-day call same strike.`, cost: "Net debit", win: "Stock pins at strike near short expiry", lose: "Stock moves away" }),
    payoff: null,
  },
  {
    id: "calendar_put", name: "Calendar Put Spread", category: "SPECIALIST",
    sentiment: "neutral", difficulty: "advanced", risk: "defined",
    plannerKind: null, net: "debit",
    short: "Put version of calendar call spread.",
    whatIs: "Sell short-dated put + buy longer-dated put. Same mechanics as calendar call.",
    whenToUse: "Neutral-to-slightly-bearish + low near-term vol.",
    legs: [
      { side: "sell", kind: "put", label: "Short-dated put" },
      { side: "buy",  kind: "put", label: "Long-dated put (same strike)" },
    ],
    maxProfit: "Limited", maxLoss: "Debit", breakeven: "Varies",
    pros: ["Positive theta"], cons: ["Vol risk"],
    example: (p) => ({ setup: `Stock at $${p.toFixed(2)}. Calendar put.`, cost: "Net debit", win: "Stock pins at strike", lose: "Stock moves away" }),
    payoff: null,
  },
  {
    id: "guts", name: "Guts", category: "SPECIALIST",
    sentiment: "vol", difficulty: "advanced", risk: "defined",
    plannerKind: null, net: "debit",
    short: "Buy ITM call + ITM put — aggressive volatility play.",
    whatIs: "Buy ITM call + ITM put. Expensive but has high delta — profits rapidly on any large move.",
    whenToUse: "High-conviction volatility event.",
    legs: [
      { side: "buy", kind: "call", label: "1 ITM call" },
      { side: "buy", kind: "put",  label: "1 ITM put" },
    ],
    maxProfit: "Unlimited 🤑",
    maxLoss:   "Debit − intrinsic value",
    breakeven: "Complex (intrinsic-based)",
    pros: ["High delta capture", "Unlimited upside"],
    cons: ["Very expensive", "IV crush brutal"],
    example: (p) => ({ setup: `Stock at $${p.toFixed(2)}. Buy ITM call + ITM put.`, cost: "Large debit", win: "Big move either way", lose: "Stock pins near middle" }),
    payoff: null,
  },
  {
    id: "short_guts", name: "Short Guts", category: "SPECIALIST",
    sentiment: "neutral", difficulty: "advanced", risk: "undefined",
    plannerKind: null, net: "credit",
    short: "Sell ITM call + ITM put — aggressive short vol, UNLIMITED risk.",
    whatIs: "Sell ITM call + ITM put. Collect huge credit but UNLIMITED risk both sides.",
    whenToUse: "Extreme low-vol conviction with massive margin.",
    legs: [
      { side: "sell", kind: "call", label: "1 ITM call" },
      { side: "sell", kind: "put",  label: "1 ITM put" },
    ],
    maxProfit: "Credit − intrinsic",
    maxLoss:   "UNLIMITED ⚠️",
    breakeven: "Complex",
    pros: ["Big credit collected"],
    cons: ["UNLIMITED both sides", "Massive margin"],
    example: (p) => ({ setup: `Stock at $${p.toFixed(2)}. Sell ITM call + ITM put.`, cost: "Large credit", win: "Stock pins at spot", lose: "Big move either way" }),
    payoff: null,
  },
];

// Quick lookup by id — used by planner pre-fill.
export const STRATEGY_BY_ID = Object.fromEntries(STRATEGIES_LIB.map(s => [s.id, s]));

// Grouped by category for the Library UI.
export const STRATEGIES_BY_CATEGORY = (() => {
  const out = {};
  for (const s of STRATEGIES_LIB) {
    (out[s.category] ||= []).push(s);
  }
  return out;
})();
